import React from "react";
import { Paper, Stack, Typography, Box, Skeleton } from "@mui/material";
import MedicationIcon from "@mui/icons-material/Medication";

import InfoButton from "../Clinical/InfoButton";
import CMStatCards from "./CMStatCards";
import PolypharmacyTable from "./PolypharmacyTable";

/**
 * Panel Polypharmacy Alert — contenedor con header, stat cards y tabla de pacientes
 * con polifarmacia (5+ medicamentos).
 *
 * @param {{
 *   data: {
 *     stats: { totalWith5Plus: number, totalWith8Plus: number, withoutRecentLabs: number },
 *     patients: Array
 *   },
 *   loading: boolean,
 *   onCreateTask: (row) => void,
 *   helpContent: object
 * }} props
 */
export default function PanelPolypharmacy({ data, loading, onCreateTask, helpContent }) {
  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack spacing={2}>
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="rectangular" animation="wave" height={300} sx={{ borderRadius: 2 }} />
        </Stack>
      </Paper>
    );
  }

  const hasPatients = data?.patients?.length > 0;

  const statItems = [
    {
      label: "On 8+ Meds",
      value: data?.stats?.totalWith8Plus,
      color: "#DC2626",
      bgColor: "#FEE2E2",
    },
    {
      label: "Without Recent Labs",
      value: data?.stats?.withoutRecentLabs,
      color: "#D97706",
      bgColor: "#FEF3C7",
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack spacing={2}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: 1.5,
              bgcolor: "#FEF3C7",
              color: "#D97706",
            }}
          >
            <MedicationIcon sx={{ fontSize: 16 }} />
          </Box>
          <Typography variant="subtitle2" fontWeight={700}>
            Polypharmacy Alert
          </Typography>
          <InfoButton helpContent={helpContent} />
        </Stack>

        {/* Main stat text */}
        <Typography variant="body2" fontWeight={600} color="text.secondary">
          {data?.stats?.totalWith5Plus ?? 0} patients on 5+ meds
        </Typography>

        {/* Sub-stats */}
        <CMStatCards items={statItems} />

        {/* Table or Empty State */}
        {hasPatients ? (
          <PolypharmacyTable patients={data.patients} onCreateTask={onCreateTask} />
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: "italic", textAlign: "center", py: 4 }}
          >
            No polypharmacy alerts
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}
